import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SidebarSection, SidebarSectionSubtitle } from '../../template-list/components/styles';
import { Button, Tooltip } from '@salecycle/ui-component-library';
import { ITemplate } from 'entities/template/template';
import styled from 'styled-components';
import ConfirmationModal from '@salecycle/experimental/ConfirmationModal';
import { useFeatureFlags } from 'core/app/AppContext';
import { useCheckTemplateUsage } from '../../../hooks/useCheckTemplateUsage';
import { useIsSaleCycleUser } from '../../../hooks/useIsSaleCycleUser';
import { useDeleteTemplate } from '../../../hooks/useDeleteTemplate';
import { isUnsubscribeTemplate } from '../../../utils/templateUtils';

const DeleteTemplateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 5px;
`;

export interface DeleteTemplateButtonProps {
  selectedTemplate: ITemplate;
}

export const DeleteTemplateButton = ({ selectedTemplate }: DeleteTemplateButtonProps) => {
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation(['common', 'designs', 'supportedLanguages', 'templates']);
  const { flagEnabled } = useFeatureFlags();
  const isDeletionEnabled = flagEnabled('ld-template-deletion', false);
  const isSaleCycleUser = useIsSaleCycleUser();
  const isUnsubscribe = isUnsubscribeTemplate(selectedTemplate);

  const { isInUse, isCheck404, isLoading: isCheckingUsage } = useCheckTemplateUsage(
    selectedTemplate,
    isDeletionEnabled,
  );

  const deleteTemplateMutation = useDeleteTemplate(selectedTemplate);

  if (!isDeletionEnabled || isUnsubscribe || !isSaleCycleUser) {
    return null;
  }

  const isDisabled =
    isInUse || isCheckingUsage || isCheck404 || deleteTemplateMutation.isLoading || !selectedTemplate.channel;

  let tooltipMessage: string | undefined;
  if (isInUse) {
    tooltipMessage = t('designs:designGallery.sidebar.deleteButtonDisabledTooltip');
  } else if (isCheck404) {
    tooltipMessage = t('designs:designGallery.sidebar.deleteButtonCheckErrorTooltip');
  } else if (!selectedTemplate.channel) {
    tooltipMessage = t('designs:designGallery.sidebar.deleteButtonNoChannelTooltip');
  }

  const handleDeleteClick = () => !isDisabled && setShowModal(true);
  const handleConfirmDelete = () => {
    setShowModal(false);
    deleteTemplateMutation.mutate();
  };
  const handleCloseModal = () => setShowModal(false);

  const button = (
    <Button variation="delete" onClick={handleDeleteClick} width={236} disabled={isDisabled}>
      {t('designs:designGallery.sidebar.deleteButton')}
    </Button>
  );

  return (
    <>
      <SidebarSection data-testid="DeleteTemplateSection">
        <SidebarSectionSubtitle data-testid="DeleteTemplateSubTitle">
          {t('designs:designGallery.sidebar.deleteSubtitle')}
        </SidebarSectionSubtitle>
        <DeleteTemplateButtonContainer>
          {tooltipMessage ? <Tooltip title={tooltipMessage}>{button}</Tooltip> : button}
        </DeleteTemplateButtonContainer>
      </SidebarSection>
      <ConfirmationModal
        isOpen={showModal}
        heading={t('designs:designGallery.sidebar.deleteConfirmation.heading')}
        description={t('designs:designGallery.sidebar.deleteConfirmation.description')}
        okButtonLabel={t('designs:designGallery.sidebar.deleteButton')}
        cancelButtonLabel={t('common.cancel')}
        onOkButtonClicked={handleConfirmDelete}
        onCancelButtonClicked={handleCloseModal}
        onCloseButtonClicked={handleCloseModal}
      />
    </>
  );
};
